import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { farZoomNow, onFarZoomChange } from '../../utils/farZoom';
import { resolveDisplayUrl } from '../../utils/displayUrl';
import { keptSmallPicture, smallPicture } from '../../utils/smallPicture';

interface CardFarViewProps {
  /** The card's main picture (utils/farZoom.ts farPictureOf); without one the card shows lines for its text */
  picture?: string;
  /** The card's body is a video: it shows a film frame */
  film?: boolean;
  userId?: string;
  workspaceName?: string;
  ownerUserId?: string;
}

/** Whether the canvas is zoomed out far (utils/farZoom.ts) */
export const useFarZoom = (): boolean => useSyncExternalStore(onFarZoomChange, farZoomNow, () => false);

/** The small copy of a picture to show, once it is wanted; the picture itself where no copy can be made */
const useSmallPicture = (ref: string | undefined, userId?: string, workspaceName?: string, ownerUserId?: string): string | null => {
  const [shown, setShown] = useState<{ ref: string; url: string } | null>(null);
  useEffect(() => {
    if (!ref) return;
    let live = true;
    void (async () => {
      // A copy kept from an earlier visit needs no lookup of the picture
      const kept = await keptSmallPicture(ref);
      if (kept) {
        if (live) setShown({ ref, url: kept });
        return;
      }
      const media = await resolveDisplayUrl(ref, { userId, workspaceName, ownerUserId });
      if (!media.url) return;
      // On the website the picture is on the cloud's domain: the small copy is made from its stored bytes
      const small = await smallPicture(ref, media.url, { userId, workspaceName, ownerUserId });
      if (live) setShown({ ref, url: small ?? media.url });
    })();
    return () => { live = false; };
  }, [ref, userId, workspaceName, ownerUserId]);
  return shown && shown.ref === ref ? shown.url : null;
};

/**
 * What a card shows in place of its body when the canvas is zoomed out far (index.css, "Cards
 * seen from far away"): its main picture, small, over grey lines for its text; or a film frame.
 * Nothing is looked up or made before the canvas is first zoomed out that far.
 */
const CardFarViewComponent: React.FC<CardFarViewProps> = ({ picture, film, userId, workspaceName, ownerUserId }) => {
  const far = useFarZoom();
  const [wanted, setWanted] = useState(far);
  if (far && !wanted) setWanted(true);
  const shown = useSmallPicture(wanted ? picture : undefined, userId, workspaceName, ownerUserId);

  return (
    <>
      {picture && (shown
        ? <img className="canvas-node-far-picture" src={shown} alt="" draggable={false} decoding="async" data-far-picture />
        : <div className="canvas-node-far-picture canvas-node-far-waiting" data-far-picture="waiting" />)}
      {!picture && film && <div className="canvas-node-far-film" data-far-film />}
      <div className="canvas-node-far-lines" />
    </>
  );
};

export const CardFarView = React.memo(CardFarViewComponent);
